import React, { useEffect, Suspense, lazy, useState } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
} from "react-router-dom";
import { makeStyles, createMuiTheme } from '@material-ui/core/styles';
import {
  CssBaseline,
  AppBar,
  Toolbar,
  Grid,
  Typography,
  ThemeProvider,
  Box,
} from '@material-ui/core'
import Login from './components/Login/Login';
import Sidebar from './components/Sidebar/Sidebar';
import LogoWhite from './assets/cbc_logo_white.svg';
import { msalInstance, authGetAcc, authWrapper, loginParameters } from './Auth/msauth';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import AdminBar from './components/PageElements/AdminBar';
import WasteJobsDashboard from './components/Waste/JobsDashboard';

const Map = lazy(() => import('./components/Maps/Map'));
const Metrics = lazy(() => import('./components/Waste/Metrics'));
const MapTruckRoute = lazy(() => import('./components/Maps/MapTruckRoute'));
const HealthCheck = lazy(() => import('./components/HealthCheck/HealthCheck'));
const Search = lazy(() => import('./components/Search/Search'));
const PropertyProfile = lazy(() => import('./components/Profile/Property'));
const CustomerProfile = lazy(() => import('./components/Profile/Customer'));
const ServiceRequestViewPage = lazy(() => import('./components/ServiceRequest/ServiceRequestViewPage'));
const ServiceRequestFormPage = lazy(() => import('./components/ServiceRequest/ServiceRequestFormPage'));
// const FormTest = lazy(() => import('./components/ServiceRequest/hooks/useForm'));


/*
* component defs
*/

function App() {
  const classes = useStyles();
  const [acc, setAcc] = useState<null | AccountInfo>(authGetAcc())
  const [auth, setAuth] = useState<null | AuthenticationResult>(null)

  useEffect(() => {
    if (acc) {
      authWrapper().then(resp => setAuth(resp))
    }
  }, [acc])


  const handleLogin = () => {
    msalInstance.loginPopup(loginParameters).then(resp => {
      setAcc(authGetAcc());
      setAuth(resp);
    })
  }

  const handleLogout = () => {
    msalInstance.logout().then(() => {
      setAuth(null);
      setAcc(null)
    })
  }

  return (
    <React.Fragment>
      <Router>
        <Suspense fallback={<div>Loading...</div>}></Suspense>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Grid className={classes.rootGrid} container direction="column">
            <Grid item>
              <AppBar position="fixed" color="primary" className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                  <div className={classes.logo} />
                  {auth?.account?.name &&
                    <Typography className={classes.menuRight}>{auth.account.name}</Typography>
                  }
                  {acc && <AdminBar handleLogout={handleLogout} />}
                </Toolbar>
              </AppBar>
            </Grid>
            {(!acc) ? (
              <Grid style={{ flex: 1 }} alignItems="center" justify="center" item container>
                <Login login={handleLogin} />
              </Grid>
            ) : (
              <Grid item>
                <main style={{ display: "flex", justifyContent: "center" }}>
                  <Sidebar logout={handleLogout} />
                  <Box width="100%" style={{ marginTop: "90px" }}>
                    <Suspense fallback={<div>Loading...</div>}>
                      <Switch>
                        {/* <Route path="/"><Redirect to="/search/#customer" /></Route> */}
                        <Route title="Health Check" path="/health" component={HealthCheck} />
                        <Route title="Map" path="/waste/map" component={Map} />
                        <Route title="Truck Routes" path="/waste/truckroute" component={MapTruckRoute} />
                        <Route title="Property Profile" path="/property/:id" component={PropertyProfile} />
                        <Route title="Customer Profile" path="/customer/:id" component={CustomerProfile} />
                        <Route title="Service Request View" path="/servicerequest/:id" component={ServiceRequestViewPage} />
                        <Route title="Create Service Request" path="/servicerequest" component={ServiceRequestFormPage} />
                        <Route title="Waste Jobs Overview" path="/waste/jobs" component={WasteJobsDashboard} />
                        <Route title="Metrics" path="/waste/metrics" component={Metrics} />
                        <Route title="Search" path="/" component={Search} />
                        {/* <Route title="Form Test" path="/form" component={FormTest} /> */}
                      </Switch>
                    </Suspense>
                  </Box>
                </main>
              </Grid>
            )}
          </Grid>
        </ThemeProvider>
      </Router>
    </React.Fragment>
  );
}

declare module "@material-ui/core/styles/createBreakpoints" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

const theme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
      xxl: 2000,
    },
  },
  palette: {
    primary: {
      main: "#1B4081",
      light: "#526ab1",
      dark: "#001b54"
    },
    secondary: {
      main: "#60c3ad",
      light: "#93f6df",
      dark: "#29927e"
    },
    info: {
      main: "#1B4081",
      dark: "#0F2958",
      light: "#5A87D7",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#8DC73F",
    },
    error: {
      dark: "#E31B0C",
      main: "#F0533E",
    }
  },
  typography: {
    h1: {
      fontSize: "2.5rem",
      color: "#1B4081",
      lineHeight: 1.12,
      fontWeight: 300,
      letterSpacing: "0.25px",
    }
  }
});

/*
* styles
*/

const useStyles = makeStyles((theme) => ({
  root: {
  },
  rootGrid: {
    minHeight: "100vh",
    backgroundColor: "#fff"
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbar: {
    height: "90px"
  },
  layout: {
    //display: 'flex',
    width: 'auto',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
      width: 1000,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    //alignItems: 'center',
    //justifyContent: 'center'
  },
  logo: {
    background: `url(${LogoWhite}) no-repeat center center`,
    backgroundSize: "contain",
    // 851 266
    width: 153.5,
    height: 48
  },
  menuRight: {
    flexGrow: 1,
    textAlign: "right"
  }
}));

export default App;
